/**
 * The P&L "matrix" grid shared by Account-Seg P&L (CC-49904) and Category P&L
 * (CC-49973).
 *
 * Both modes get the SAME payload shape from `GetPLReport` — the pivot columns
 * arrive in `report.accountSegmentInfo`, the per-account cells in each line
 * item's `segmentTotalPerAccount`, and the per-section column totals in each
 * section's `accountSegmentTotals`. Only the framing differs, so one builder
 * serves both and the two modes cannot drift apart.
 *
 * ─── Payload facts, verified live on vladdemo 2026-08-10 ────────────────────
 * Do not re-derive these from the C#; they were captured from real responses.
 *
 * 1. `accountSegmentInfo` ALWAYS carries a sentinel entry with
 *    `segmentId === -1 && segmentValueId === -1`, labelled "All others". It has
 *    NO matching cell in any row's `segmentTotalPerAccount`, so it can never be
 *    rendered as an ordinary column — V1 filters it out
 *    (AccountingPAndLReportView.js:523). We drop it in BOTH modes.
 *
 * 2. The row total is `totalAmount`, **not** `amount`. V1 renders
 *    `account.TotalAmountString` as the closing cell (:869). The identity is:
 *        totalAmount === Σ(segmentTotalPerAccount) + allOthersAmount
 *    Live example (segment = Location, Fees Received, all dates, accrual):
 *        segments 2,894,437.68 + allOthers 13,665,739.90 = 16,560,177.58 = totalAmount
 *        …while `amount` was 13,898,891.41 — a DIFFERENT number.
 *    Using `amount` here (what cb2 shipped) put a figure in the Total column
 *    that equalled neither the row's own total nor the sum of its visible cells.
 *
 * 3. Section totals follow the same identity:
 *        sectionTotal === Σ(accountSegmentTotals where segmentValueId > 0)
 *                         + (accountSegmentTotals entry for segmentValueId -1)
 *    Live (segment = Location, Income): 2,894,437.68 + 15,104,734.68 = 17,999,172.36.
 *    In CATEGORY mode there is NO -1 entry at all, so the sum of the category
 *    columns already equals sectionTotal (live: Expenses 174,973 + 141,062 +
 *    196,569 + 192,438 + 177,353 = 882,395 = sectionTotal).
 *
 * 4. Per-column Net Income is server-provided on each `accountSegmentInfo`
 *    entry (`netIncome`), which is what V1's
 *    `addImportantNumberDisplayAccountSeg` renders. We pass it through.
 *
 * ─── Mode differences (V1 parity) ──────────────────────────────────────────
 * segment  → an explicit "All others" column (V1 passes showAllOthers=true at
 *            :447) then the closing column.
 * category → NO All-others column (V1 passes showAllOthers=false at :553).
 *
 * The closing column is labelled **"Firm" in both modes**, which is what V1 does
 * (`:435` for segment, `:542` for category — the two lines are identical).
 *
 * ⚠️ In category mode with only SOME categories selected, `allOthersAmount` is
 * non-zero but hidden, so "Firm" can exceed the sum of the visible category
 * columns. That is V1's behaviour, kept deliberately — the closing column is the
 * account's firm-wide total, not a row sum. The caller surfaces this in the
 * column tooltip so a reader never mistakes it for the sum of what they see.
 *
 * NOTHING here is recomputed: every figure is a server value passed through.
 * The only arithmetic is choosing WHICH server field belongs in which cell.
 */

/** Money format shared with PLReport — zero renders blank, negatives in parens. */
export const fmtMoney = (v) => {
  const n = Number(v ?? 0)
  if (n === 0) return ''
  const fmt = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `($${fmt})` : `$${fmt}`
}

/** displayValue / groupHeaderContent are injected as HTML by the universal grid
 *  (dangerouslySetInnerHTML) — escape ALL raw data placed in them. */
export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// CB serializes ProfitLossSection.SectionName as a struct {Account, Name,
// Description, Order}; the C# `Name` getter flattens it to a string. Always
// prefer `sec.name`/`sec.accountName` (strings); only fall back to the struct.
export const sectionLabel = (sec) =>
  (typeof sec.name === 'string' && sec.name)
  || (typeof sec.accountName === 'string' && sec.accountName)
  || sec.sectionName?.description
  || sec.sectionName?.name
  || (typeof sec.sectionName === 'string' ? sec.sectionName : '')

/** A pivot column that carries no per-account cells and must never be rendered
 *  as one. See fact 1 above. */
export const isSentinelSegment = (s) =>
  Number(s?.segmentId) === -1 || Number(s?.segmentValueId) === -1

/**
 * Is there anything to pivot on? The matrix needs at least one NON-sentinel
 * column: with only the -1 "All others" entry there are no per-account cells, so
 * the caller must fall back to the standard statement instead of rendering a
 * matrix with no amount columns. Deliberately the same predicate the builder
 * filters with — testing the two differently is how you get an "active" matrix
 * whose only columns are Account and Total.
 */
export const hasPivotColumns = (report) =>
  (report?.accountSegmentInfo || []).some((s) => !isSentinelSegment(s))

const ALL_OTHERS = 'allOthers'
const CLOSING = 'total'
const ACCOUNT_NUMBER = 'accountNumber'

/**
 * @param {object}  args
 * @param {object}  args.report      the P&L report payload
 * @param {Array}   args.sections    report.sections (already memoized upstream)
 * @param {number}  args.netIncome   report-level net income
 * @param {'segment'|'category'} args.mode
 * @returns {{columns:Array, reportData:Array, reportFooterCells:Array}} grid report
 */
export function buildSegmentMatrix({ report, sections = [], netIncome, mode = 'segment' }) {
  const isCategory = mode === 'category'
  const segCols = (report?.accountSegmentInfo || []).filter((s) => !isSentinelSegment(s))
  // The -1 entry of a section's accountSegmentTotals IS the All-others total.
  const allOthersSectionTotal = (sec) =>
    (sec?.accountSegmentTotals || []).find((t) => Number(t.segmentValueId) === -1)?.segmentTotal

  const columns = [
    // Spec 127 A2: the same "Account Number" TEXT column as the standard statement
    // — CB's `accountNumber` (on every line), no dataValue, blank when absent.
    { columnId: ACCOUNT_NUMBER, label: 'Account Number', visible: true, columnAlignment: 'LEFT' },
    { columnId: 'account', label: 'Account', visible: true, columnAlignment: 'LEFT' },
    ...segCols.map((s, i) => ({
      columnId: `seg${i}`,
      label: s.label || `Segment ${i + 1}`,
      visible: true,
      columnAlignment: 'RIGHT',
    })),
    ...(isCategory ? [] : [{
      columnId: ALL_OTHERS,
      label: 'All others',
      visible: true,
      columnAlignment: 'RIGHT',
    }]),
    {
      columnId: CLOSING,
      // V1 labels this column "Firm" in BOTH branches — the segment view at
      // `:435` and the category view at `:542`, byte-identical. cb2 shipped
      // "Total" on the segment side, so the two modes disagreed by one word for
      // the same measure. It is also the more accurate word: this cell is the
      // account's FIRM-WIDE total, not the sum of the columns beside it (see the
      // partial-selection note above), and "Total" invites a reader to check an
      // addition that is not supposed to hold.
      label: 'Firm',
      visible: true,
      columnAlignment: 'RIGHT',
    },
  ]

  // segmentValueId → column key, so a line's segmentTotalPerAccount maps to the
  // right column regardless of ordering (the payload's cell order and the
  // accountSegmentInfo order do NOT match).
  const colForSegValue = new Map(segCols.map((s, i) => [String(s.segmentValueId), `seg${i}`]))

  const segCells = (byId) => segCols.map((s, i) => {
    const v = byId.get(`seg${i}`)
    return { columnId: `seg${i}`, displayValue: v != null ? fmtMoney(v) : '', dataValue: v ?? 0 }
  })

  const rowSegMap = (li) => {
    const byCol = new Map()
    for (const seg of li.segmentTotalPerAccount || []) {
      const key = colForSegValue.get(String(seg.segmentValueId))
      if (key) byCol.set(key, Number(seg.accountSegmentAmount || 0))
    }
    return byCol
  }

  const reportData = sections.map((sec) => {
    const label = sectionLabel(sec)
    const group = {
      isGroup: true,
      groupHeaderContent: esc(label),
      dataRows: (sec.lineItems || [])
        // V1 skips the -999 sentinel row outright (:824).
        .filter((li) => Number(li.accountId) !== -999)
        .map((li) => ({
          isGroup: false,
          dataCells: [
            // Text, never a number: no dataValue (CC-53904's family — "0315" must
            // not become 315, "10101" must not become "10,101").
            { columnId: ACCOUNT_NUMBER, displayValue: esc(li.accountNumber ?? '') },
            { columnId: 'account', displayValue: esc(li.accountName || li.name || '') },
            ...segCells(rowSegMap(li)),
            ...(isCategory ? [] : [{
              columnId: ALL_OTHERS,
              displayValue: fmtMoney(li.allOthersAmount),
              dataValue: Number(li.allOthersAmount || 0),
            }]),
            {
              columnId: CLOSING,
              // fact 2: totalAmount, never `amount`.
              displayValue: fmtMoney(li.totalAmount),
              dataValue: Number(li.totalAmount || 0),
            },
          ],
        })),
    }

    const secByCol = new Map()
    for (const t of sec.accountSegmentTotals || []) {
      const key = colForSegValue.get(String(t.segmentValueId))
      if (key) secByCol.set(key, Number(t.segmentTotal || 0))
    }
    const secAllOthers = allOthersSectionTotal(sec)
    group.groupFooterCells = [
      { columnId: 'account', displayValue: `Total ${esc(label)}` },
      ...segCells(secByCol),
      ...(isCategory ? [] : [{
        columnId: ALL_OTHERS,
        displayValue: secAllOthers != null ? fmtMoney(secAllOthers) : '',
      }]),
      { columnId: CLOSING, displayValue: sec.sectionTotal != null ? fmtMoney(sec.sectionTotal) : '' },
    ]
    return group
  })

  // Net Income row. Per-column values are server-provided (fact 4) — V1 renders
  // exactly these. The closing cell keeps the report-level figure and its
  // profit/loss colour semantics.
  const reportFooterCells = netIncome != null
    ? [
        { columnId: 'account', displayValue: 'Net Income' },
        ...segCols.map((s, i) => ({
          columnId: `seg${i}`,
          displayValue: s.netIncome != null ? fmtMoney(s.netIncome) : '',
        })),
        ...(isCategory ? [] : [{
          columnId: ALL_OTHERS,
          displayValue: fmtMoney(
            (report?.accountSegmentInfo || []).find(isSentinelSegment)?.netIncome
          ),
        }]),
        {
          columnId: CLOSING,
          displayValue: `<span class="${Number(netIncome) >= 0 ? 'text-cb-teal-dark' : 'text-red-600'}">${fmtMoney(netIncome)}</span>`,
        },
      ]
    : []

  return { columns, reportData, reportFooterCells }
}

export default buildSegmentMatrix
